import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { apiFetch } from '../fetchInterceptor';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft, Plus, Trash2, ChevronUp, ChevronDown, FolderTree,
  Save, Loader2, CheckCircle2, FolderPlus, Folder, Lock, GitBranch,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INDUSTRIES = ['tax', 'accounting', 'marketing', 'legal', 'hr', 'real_estate', 'construction', 'manufacturing', 'healthcare', 'custom'];
const CLIENT_TYPES = ['individual', 'small_business', 'enterprise', 'nonprofit', 'custom'];
const COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6', '#06b6d4',
  '#ef4444', '#22c55e', '#6366f1', '#64748b',
];

const tmpId = () => `tmp_${Math.random().toString(36).slice(2, 10)}`;

export default function FolderTemplateForm() {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.workspace_id;
  const isEdit = Boolean(templateId);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [isSystem, setIsSystem] = useState(false);
  const [version, setVersion] = useState(1);
  const [parentTemplateId, setParentTemplateId] = useState(null);

  const [form, setForm] = useState({
    name: '',
    description: '',
    industry: 'custom',
    client_type: 'custom',
    color: '#3b82f6',
    nodes: [
      { node_id: tmpId(), name: 'Folder 1', parent_id: null, order_index: 0, color: '#3b82f6' },
    ],
  });

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const res = await apiFetch(`${API_URL}/api/folder-templates/${templateId}`);
        if (!res.ok) {
          toast.error('Template not found');
          navigate('/folder-templates');
          return;
        }
        const t = await res.json();
        setIsSystem(Boolean(t.is_system));
        setVersion(t.version || 1);
        setParentTemplateId(t.parent_template_id || null);
        setForm({
          name: t.name || '',
          description: t.description || '',
          industry: t.industry || 'custom',
          client_type: t.client_type || 'custom',
          color: t.color || '#3b82f6',
          nodes: (t.nodes || []).map(n => ({ ...n, node_id: n.node_id || tmpId() })),
        });
      } catch {
        toast.error('Failed to load template');
      } finally {
        setLoading(false);
      }
    })();
  }, [isEdit, templateId, navigate]);

  // Build a tree from flat node list for rendering
  const tree = useMemo(() => {
    const byParent = new Map();
    form.nodes.forEach(n => {
      const key = n.parent_id || '__root__';
      if (!byParent.has(key)) byParent.set(key, []);
      byParent.get(key).push(n);
    });
    for (const arr of byParent.values()) {
      arr.sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
    }
    return byParent;
  }, [form.nodes]);

  const addRoot = () => {
    const count = form.nodes.filter(n => !n.parent_id).length;
    setForm({
      ...form,
      nodes: [...form.nodes, { node_id: tmpId(), name: `Folder ${count + 1}`, parent_id: null, order_index: count, color: form.color }],
    });
  };

  const addChild = (parentId) => {
    const siblings = form.nodes.filter(n => n.parent_id === parentId).length;
    setForm({
      ...form,
      nodes: [...form.nodes, { node_id: tmpId(), name: 'Subfolder', parent_id: parentId, order_index: siblings, color: form.color }],
    });
  };

  const updateNode = (nodeId, patch) => {
    setForm({ ...form, nodes: form.nodes.map(n => n.node_id === nodeId ? { ...n, ...patch } : n) });
  };

  const removeNode = (nodeId) => {
    // Remove node and any descendants
    const toRemove = new Set([nodeId]);
    let added = true;
    while (added) {
      added = false;
      for (const n of form.nodes) {
        if (n.parent_id && toRemove.has(n.parent_id) && !toRemove.has(n.node_id)) {
          toRemove.add(n.node_id);
          added = true;
        }
      }
    }
    setForm({ ...form, nodes: form.nodes.filter(n => !toRemove.has(n.node_id)) });
  };

  const moveNode = (nodeId, direction) => {
    const node = form.nodes.find(n => n.node_id === nodeId);
    if (!node) return;
    const siblings = form.nodes
      .filter(n => n.parent_id === node.parent_id)
      .sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
    const idx = siblings.findIndex(n => n.node_id === nodeId);
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= siblings.length) return;
    const swap = siblings[newIdx];
    setForm({
      ...form,
      nodes: form.nodes.map(n => {
        if (n.node_id === node.node_id) return { ...n, order_index: newIdx };
        if (n.node_id === swap.node_id) return { ...n, order_index: idx };
        return n;
      }),
    });
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Template name is required'); return; }
    if (form.nodes.length === 0) { toast.error('Add at least one folder'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        scope: 'workspace',
        // Send clean node_ids only (server will normalize tmp_*)
        nodes: form.nodes.map(({ node_id, ...rest }) => ({
          ...rest,
          node_id: node_id?.startsWith('tmp_') ? undefined : node_id,
        })),
      };
      const url = isEdit
        ? `${API_URL}/api/folder-templates/${templateId}?workspace_id=${wsId}`
        : `${API_URL}/api/folder-templates?workspace_id=${wsId}`;
      const res = await apiFetch(url, {
        method: isEdit ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success(isEdit ? 'Template updated' : 'Template created');
        navigate('/folder-templates');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save');
      }
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-6 text-center text-muted-foreground">Loading…</div>;

  // Recursive render of the node tree
  const renderTree = (parentId = null, depth = 0) => {
    const children = tree.get(parentId || '__root__') || [];
    return children.map((node, idx) => (
      <div key={node.node_id}>
        <div
          className={cn(
            'group flex items-center gap-2 py-2 px-2 rounded-lg hover:bg-muted/50',
            depth === 0 ? 'border-l-2 border-primary/30 mt-2' : 'ml-6'
          )}
          style={{ paddingLeft: depth === 0 ? '0.5rem' : `${0.5 + depth * 1.25}rem` }}
        >
          <Folder className="w-4 h-4 flex-shrink-0" style={{ color: node.color || form.color }} />
          <Input
            value={node.name}
            onChange={(e) => updateNode(node.node_id, { name: e.target.value })}
            placeholder="Folder name"
            className="h-8 flex-1 text-sm border-0 bg-transparent focus-visible:ring-1"
            data-testid={`node-name-${idx}`}
            disabled={isSystem}
          />
          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <input
              type="color"
              value={node.color || form.color}
              onChange={(e) => updateNode(node.node_id, { color: e.target.value })}
              className="w-6 h-6 rounded border cursor-pointer"
              disabled={isSystem}
              title="Folder color"
            />
            <Button size="icon" variant="ghost" className="h-7 w-7"
              onClick={() => moveNode(node.node_id, 'up')} disabled={isSystem}
              title="Move up">
              <ChevronUp className="w-3.5 h-3.5" />
            </Button>
            <Button size="icon" variant="ghost" className="h-7 w-7"
              onClick={() => moveNode(node.node_id, 'down')} disabled={isSystem}
              title="Move down">
              <ChevronDown className="w-3.5 h-3.5" />
            </Button>
            <Button size="icon" variant="ghost" className="h-7 w-7"
              onClick={() => addChild(node.node_id)} disabled={isSystem}
              title="Add subfolder"
              data-testid={`add-subfolder-${node.node_id}`}>
              <FolderPlus className="w-3.5 h-3.5" />
            </Button>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive"
              onClick={() => removeNode(node.node_id)} disabled={isSystem}
              title="Remove">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
        {renderTree(node.node_id, depth + 1)}
      </div>
    ));
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="folder-template-form-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/folder-templates')} data-testid="template-form-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <FolderTree className="w-6 h-6" />
            {isEdit ? (isSystem ? 'View System Template' : 'Edit Template') : 'New Folder Template'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isSystem
              ? 'This is a system preset — duplicate it to make a custom version.'
              : 'Define a reusable folder structure. Apply it later to any workspace.'}
          </p>
        </div>
        {isSystem && (
          <Badge variant="outline" className="text-[10px] bg-amber-100 text-amber-700 border-amber-300">
            <Lock className="w-2.5 h-2.5 mr-1" /> READ-ONLY
          </Badge>
        )}
        {version > 1 && (
          <Badge className="text-[10px] bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
            <GitBranch className="w-2.5 h-2.5 mr-1" /> v{version}
          </Badge>
        )}
      </div>

      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Annual Tax Engagement" disabled={isSystem}
                data-testid="template-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Default Folder Color</Label>
              <div className="flex items-center gap-1">
                {COLORS.map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setForm({ ...form, color: c })}
                    disabled={isSystem}
                    className={cn('w-6 h-6 rounded-full border-2 transition-all', form.color === c ? 'ring-2 ring-offset-1 ring-primary scale-110' : 'border-transparent')}
                    style={{ background: c }}
                    title={c}
                    data-testid={`color-${c}`}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="What does this template organize?" rows={2} disabled={isSystem} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Industry</Label>
              <Select value={form.industry} onValueChange={(v) => setForm({ ...form, industry: v })} disabled={isSystem}>
                <SelectTrigger data-testid="industry-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {INDUSTRIES.map(i => <SelectItem key={i} value={i} className="capitalize">{i.replace('_', ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Client Type</Label>
              <Select value={form.client_type} onValueChange={(v) => setForm({ ...form, client_type: v })} disabled={isSystem}>
                <SelectTrigger data-testid="client-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CLIENT_TYPES.map(c => <SelectItem key={c} value={c} className="capitalize">{c.replace('_', ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Folder tree editor */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Folder Structure</Label>
              <p className="text-[11px] text-muted-foreground mt-1">
                Hover a row to reorder, change color, add subfolder, or delete. Drag-and-drop is coming in a future update.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={addRoot} disabled={isSystem} data-testid="add-root-folder-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Top-Level Folder
            </Button>
          </div>

          <div className="border rounded-xl p-2 bg-muted/20 min-h-[160px]">
            {form.nodes.length === 0
              ? <div className="text-center text-xs text-muted-foreground py-8">No folders yet. Click "Add Top-Level Folder" to get started.</div>
              : renderTree(null, 0)
            }
          </div>
        </CardContent>
      </Card>

      <div className="sticky bottom-0 -mx-4 sm:mx-0 bg-card/95 backdrop-blur border-t px-4 sm:px-0 py-3 flex justify-end gap-3 z-10">
        <Button variant="outline" onClick={() => navigate('/folder-templates')} data-testid="template-form-cancel-btn">Cancel</Button>
        {!isSystem && (
          <Button onClick={handleSave} disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700 text-white min-w-[160px]"
            data-testid="save-template-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : (isEdit ? <Save className="w-4 h-4 mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />)}
            {isEdit ? 'Save Changes' : 'Create Template'}
          </Button>
        )}
        {parentTemplateId && (
          <span className="text-[10px] text-muted-foreground self-center">Derived from {parentTemplateId}</span>
        )}
      </div>
    </div>
  );
}
